import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from 'store';

import { featureFlags } from 'common/featureFlags/featureFlags';
import type { UserStudies, UserStudySite } from 'common/graphql/types';
import { CILogger } from 'common/log/log.provider';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';

import { AuthActions } from '../actions';
import { fillUserRecord } from '../helpers';
import { switchUserRoleDocumentNode } from '../queries';

export const useUpdateActiveStudySiteWithRole = () => {
    const activeUser = useSelector((state: RootState) => state.auth.activeUser);
    const userRoleSwitchPending = useSelector((state: RootState) => state.auth.userRoleSwitchPending);

    const dispatch = useDispatch();
    const [switchUserRoleMutation] = useMutation(switchUserRoleDocumentNode);

    const updateActiveStudySite = useCallback(
        async ({ study, site }: { study: UserStudies; site: UserStudySite }) => {
            CILogger.getInstance().info(`Updating active study: ${study?.studyName} site: ${site?.siteName}`);

            if (!featureFlags.fxIntegration || activeUser.authenticationMethod === 'COGNITO') {
                dispatch(
                    AuthActions.updateActiveStudySite({
                        site,
                        study,
                    })
                );

                return;
            }

            try {
                CIPageStatus.dismiss();
                dispatch(AuthActions.updateUserRoleSwitchPending(true));
                await switchUserRoleMutation({
                    variables: {
                        userId: activeUser.pk,
                        subscriberId: activeUser.subscriberPk,
                        roleId: study.roleVClaim.sk,
                        fxRoleId: study.roleVClaim.fxId,
                    },
                });

                try {
                    const updatedUser = await fillUserRecord(activeUser);
                    dispatch(
                        AuthActions.updateActiveStudySite({
                            site,
                            study,
                            updatedUser,
                        })
                    );
                } catch (reloadError) {
                    const originalRoleClaim = activeUser.roleBClaim || activeUser.roleVClaim;
                    if (originalRoleClaim) {
                        try {
                            await switchUserRoleMutation({
                                variables: {
                                    userId: activeUser.pk,
                                    subscriberId: activeUser.subscriberPk,
                                    roleId: originalRoleClaim.sk,
                                    fxRoleId: originalRoleClaim.fxId,
                                },
                            });
                        } catch (revertError) {
                            CILogger.getInstance().error(
                                'Failed to revert role switch to the original role',
                                revertError
                            );
                        }
                    }
                    throw reloadError;
                }
            } catch (e) {
                CILogger.getInstance().error('Error updating active study site - updateActiveStudySite', e);
                throw e;
            } finally {
                dispatch(AuthActions.updateUserRoleSwitchPending(false));
            }
        },
        [switchUserRoleMutation, dispatch, activeUser]
    );

    return [updateActiveStudySite, { loading: userRoleSwitchPending }] as const;
};
